import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { Fragment, useState, useEffect, useCallback, useRef } from "react";
import { merge } from "./object.js";
import { toKebabCase } from "./string.js";
import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
export function useSafeUpdate(updateState) {
  var isMounted = useRef(true);
  var updateStateRef = useRef();
  updateStateRef.current = updateState;
  var safeUpdateState = useCallback(function (value) {
    if (isMounted.current) {
      updateStateRef.current(value);
    }
  }, []);
  useEffect(function () {
    return function () {
      isMounted.current = false;
    };
  }, []);
  return safeUpdateState;
}
export function useToggleVisibility() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var initialState = options.initialState,
    onOpen = options.onOpen,
    afterOpen = options.afterOpen,
    onClose = options.onClose,
    afterClose = options.afterClose,
    onChange = options.onChange;
  var _useState = useState(initialState ? initialState : false),
    _useState2 = _slicedToArray(_useState, 2),
    isComponentVisible = _useState2[0],
    __setComponentVisible = _useState2[1];
  var _setComponentVisible = useCallback(function (newVisibility) {
    if (typeof onOpen === "function" && !isComponentVisible && newVisibility) {
      onOpen();
    }
    if (typeof onClose === "function" && isComponentVisible && !newVisibility) {
      onClose();
    }
    if (typeof onChange === "function" && isComponentVisible !== newVisibility) {
      onChange({
        isOpen: newVisibility
      });
    }
    __setComponentVisible(newVisibility);
    if (typeof afterOpen === "function" && !isComponentVisible && newVisibility) {
      setTimeout(afterOpen, 0);
    }
    if (typeof afterClose === "function" && !isComponentVisible && newVisibility) {
      setTimeout(afterClose, 0);
    }
  }, [isComponentVisible, onOpen, afterOpen, onClose, afterClose, onChange]);
  var setComponentVisible = useSafeUpdate(_setComponentVisible);
  var toggleComponentVisible = useCallback(function () {
    setComponentVisible(!isComponentVisible);
  }, [isComponentVisible, setComponentVisible]);
  return {
    isComponentVisible: isComponentVisible,
    setComponentVisible: setComponentVisible,
    toggleComponentVisible: toggleComponentVisible
  };
}
export var substituteFromReact = function substituteFromReact() {
  var template = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : "";
  var data = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var lastId = 0;
  var children = template
  // Replace linked text ('[...]') with substitution markers
  .replace(/\[(.*?)\]/g, function (_, part) {
    var onLinkedTextClick = options.onLinkedTextClick,
      href = options.href;
    if (onLinkedTextClick) {
      var identifier = "__linkIdentifier_".concat(++lastId);
      data = merge(data, _defineProperty({}, identifier, /*#__PURE__*/_jsx("span", {
        className: "__linked-text",
        onClick: onLinkedTextClick,
        children: part
      })));
      return "{".concat(identifier, "}");
    } else if (href) {
      var _identifier = "__linkIdentifier_".concat(++lastId);
      data = merge(data, _defineProperty({}, _identifier, /*#__PURE__*/_jsx("a", {
        className: "__linked-text",
        href: href,
        children: part
      })));
      return "{".concat(_identifier, "}");
    } else {
      return part;
    }
  })

  // Replace substitution markers ('{...}')
  .split(/(\{.*?\})/).map(function (part, index) {
    var partWithoutBrackets = part.slice(1, -1);
    var className = "substituted-".concat(toKebabCase(partWithoutBrackets));
    var substitution = /^\{(.*)\}$/.test(part) ? /*#__PURE__*/_jsx("span", {
      className: className,
      children: data[partWithoutBrackets]
    }) || "" : part;
    return /*#__PURE__*/_jsx(Fragment, {
      children: substitution
    }, index);
  });
  return /*#__PURE__*/_jsx(_Fragment, {
    children: children
  });
};